import { Injectable } from '@angular/core';
import { ShopPluginApi } from '@app/core/services/api/plugin-api.service';
import { BehaviorSubject, forkJoin, Observable, ReplaySubject } from 'rxjs';
import { ShopPlugin } from '@pdqdev/pdq-shared/dist/interfaces';
import { Shop } from '@services/shops/shop.model';
import { finalize, map } from 'rxjs/operators';
import { ToastService } from '@storybook/services/toast/toast.service';
import { capitalize } from '@utils/string-utils';
import { CheckoutApiService } from '@app/pages/checkout-app/services';
import { ITrackingPage } from '@pdqdev/pdq-shared/dist/interfaces/shopPlugin/ITrackingPage';
import { Router } from '@angular/router';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { Track360Api } from '@app/core/services/api/track360-api.service';

@Injectable({
  providedIn: 'root',
})
export class TrackingPageService {
  dynamicFieldsAutocomplete$ = new BehaviorSubject<any>(null);

  constructor(
    private router: Router,
    private pluginApi: ShopPluginApi,
    private checkoutApiService: CheckoutApiService,
    private toastService: ToastService,
    private track360Api: Track360Api,
  ) {
    this.initTrackingPageConfiguration();
    this.initTrackingPageMetrics();
  }
  private _trackingPageConfiguration = new ReplaySubject<ShopPlugin.TrackingPage.Configuration>(1);
  readonly trackingPageConfiguration$ = this._trackingPageConfiguration.asObservable();

  private _trackingPageMetrics = new ReplaySubject<ShopPlugin.TrackingPage.Metrics>(1);
  readonly trackingPageMetrics$ = this._trackingPageMetrics.asObservable();

  private _isConfigurationLoading = new BehaviorSubject(true);
  readonly isConfigurationLoading$ = this._isConfigurationLoading.asObservable();

  private _isTrackingPageMetricsLoading = new BehaviorSubject(true);
  readonly isTrackingPageMetricsLoading$ = this._isTrackingPageMetricsLoading.asObservable();

  set trackingPageConfiguration(conf: ShopPlugin.TrackingPage.Configuration) {
    this._trackingPageConfiguration.next(conf);
  }

  initTrackingPageConfiguration() {
    this._isConfigurationLoading.next(true);
    this.pluginApi.get('/admin/tracking-page/configuration').subscribe(
      (response: ShopPlugin.TrackingPage.Configuration) => {
        this._isConfigurationLoading.next(false);
        this._trackingPageConfiguration.next(response);
      },
      () => {
        this.toastService.showToast({ mode: 'danger', message: 'Failed to load tracking page configuration' });
        this.router.navigate(['/convert/checkout-groups']);
      },
    );
  }

  initTrackingPageMetrics() {
    this.pluginApi
      .get('/admin/tracking-page/metrics')
      .pipe(finalize(() => this._isTrackingPageMetricsLoading.next(false)))
      .subscribe((response: ShopPlugin.TrackingPage.Metrics) => {
        this._trackingPageMetrics.next(response);
      });
  }

  saveTrackingPageConfiguration(conf: ShopPlugin.TrackingPage.Configuration, shop: Shop) {
    return this.pluginApi.post('/admin/tracking-page/configuration', {
      ...conf,
      shop: { id: shop.id, name: shop.name },
    });
  }

  getCarrierOptions(): Observable<DropdownOption[]> {
    return this.track360Api.get('/carrier/options-list').pipe(
      map((response) =>
        response
          .filter((carrier) => !!carrier?.label && !!carrier?.value)
          .map((carrier) => ({
            label: carrier.label,
            value: carrier.value,
          })),
      ),
    );
  }

  get dynamicFieldsAutocompleteValues(): string[] {
    return this.dynamicFieldsAutocomplete$.getValue();
  }

  initDynamicFieldsAutocomplete(): void {
    if (!this.dynamicFieldsAutocompleteValues) {
      const metafieldDefinitions$: Observable<
        { name: string; description: string; category: string; displayName: string }[]
      > = this.checkoutApiService.get('admin/v1/shops/metafield-definitions/autocomplete');

      forkJoin([metafieldDefinitions$]).subscribe(
        ([metafieldDefinitions]) => {
          // If metafieldDefinitions is null or empty, fallback to initialDynamicFieldsVariables
          const fetchedMetafields = metafieldDefinitions && metafieldDefinitions.length > 0 ? metafieldDefinitions : [];

          // Merge the fetched metafields with the initial ones
          let options = [...ITrackingPage.initialDynamicFieldsVariables, ...fetchedMetafields].sort((a, b) =>
            a.name.localeCompare(b.name),
          );

          options = options.map((option) => {
            return {
              name: option.name,
              searchTerm: option.name.replace(/[{}]/g, ''),
              description: capitalize(option.description),
              category: option.category,
              displayName: option.displayName,
            };
          });

          this.dynamicFieldsAutocomplete$.next(options);
        },
        (error) => {
          console.error('Failed to init metafield definitions', error);

          // In case of error, still set the initial values
          this.dynamicFieldsAutocomplete$.next(ITrackingPage.initialDynamicFieldsVariables);
        },
      );
    }
  }
}
